// Profile view — avatar, name, email and sign-out, in a dialog over the
// match. Account management itself lives on forge-auth.

import { Button } from '@aod/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@aod/ui/components/ui/dialog';
import { LogOut } from 'lucide-react';
import type { SessionUser } from '../../auth/session.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import { UserAvatar } from './UserAvatar.js';

interface ProfileDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly user: SessionUser;
  readonly onSignOut: () => void | Promise<void>;
}

export function ProfileDialog({ open, onOpenChange, user, onSignOut }: ProfileDialogProps) {
  const t = useT();
  const displayName = user.name ?? t('profile.unnamed');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="z-[110] sm:max-w-sm"
        data-testid="profile-view"
        {...appDom({ id: 'profile-view', type: 'Modal', scope: 'dialog', label: 'Profile' })}
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <UserAvatar user={user} className="size-20 text-2xl" />
          <DialogTitle className="mt-2 text-lg">{displayName}</DialogTitle>
          <DialogDescription>{t('profile.title')}</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm">
          <dt className="text-muted-foreground">{t('profile.name')}</dt>
          <dd className="m-0 truncate">{user.name ?? '—'}</dd>
          <dt className="text-muted-foreground">{t('profile.email')}</dt>
          <dd className="m-0 truncate">{user.email ?? '—'}</dd>
        </dl>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('profile.close')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            data-testid="profile-sign-out"
            {...appDom({ id: 'profile-sign-out', type: 'Button', role: 'button', scope: 'dialog' })}
            onClick={() => void onSignOut()}
          >
            <LogOut />
            {t('auth.signOut')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
